import {useEffect, useRef} from 'react';
import {injectScript} from '../Helpers/ScriptInjectionHelpers';

// The chatbot script renders invisible reCAPTCHA widgets and needs the Google API on the page.
const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js';

declare global {
  interface Window {
    RvdChatbot?: {
      init: () => void;
      destroy: () => void;
      hide: () => void;
      show: () => void;
    };
  }
}

// Removal is deferred one tick so that navigating between two pages that both use the
// widget (unmount + mount in the same commit) keeps it alive — an open conversation is
// not torn down and rebuilt. The timeout only fires when no widget page follows.
let pendingDestroy: number | null = null;

const cancelPendingDestroy = (): void => {
  if (pendingDestroy !== null) {
    window.clearTimeout(pendingDestroy);
    pendingDestroy = null;
  }
};

/**
 * Mounts the per-client third-party chat widget (public/chatbot/rvd-chatbot.js) while the
 * component is on screen, and removes it once no opted-in page is mounted, so the widget
 * exists only on the pages that opt in. The script URL comes from the client's
 * CuratedSiteConfig configuration item (chatbotScriptUrl) — clients whose value is missing,
 * empty or whitespace never load anything.
 *
 * Pass hidden=true to conceal the widget without unloading it (e.g. while a payment iframe
 * is displayed); flipping back to false restores it with the conversation intact.
 */
export default function useThirdPartyChatbot(url: string | null | undefined, hidden: boolean = false): void {
  const hiddenRef = useRef(hidden);
  const scriptUrl = url?.trim() || null;

  useEffect(() => {
    hiddenRef.current = hidden;
    if (!scriptUrl) {
      return;
    }

    try {
      if (hidden) {
        window.RvdChatbot?.hide();
      } else {
        window.RvdChatbot?.show();
      }
    } catch (err) {
      console.error('Failed to toggle the chat widget', err);
    }
  }, [scriptUrl, hidden]);

  useEffect(() => {
    if (!scriptUrl) {
      return;
    }

    cancelPendingDestroy();
    injectScript(RECAPTCHA_SRC);
    injectScript(scriptUrl, {
      onLoad: () => {
        try {
          window.RvdChatbot?.init();
          if (hiddenRef.current) {
            window.RvdChatbot?.hide();
          }
        } catch (err) {
          console.error('Failed to initialize the chat widget', err);
        }
      }
    });

    return () => {
      cancelPendingDestroy();
      pendingDestroy = window.setTimeout(() => {
        pendingDestroy = null;
        try {
          window.RvdChatbot?.destroy();
        } catch (err) {
          console.error('Failed to remove the chat widget', err);
        }
      });
    };
  }, [scriptUrl]);
}
